<template>
  <div v-if="images">
    <div class="relative">
      <div
        v-if="!isMobile"
        :class="computedMosaicGridClasses"
      >
        <div
          v-for="(photo, index) in mosaicPhotos"
          :key="(photo as SwipeImage)?.src || (photo as SwipeVideo).source"
          :class="[
            'relative bg-black',
            mosaicGridAreaClass[index],
            index === 0 ? computedAspectRatioClass : '',
          ]"
        >
          <img
            v-if="(photo as SwipeImage).src"
            :src="(photo as SwipeImage).src"
            alt=""
            class="w-full h-full absolute inset-0 object-cover hover:opacity-85 transition-all cursor-pointer"
            @click="openLightBox(index)"
          >
          <LazyAppVideoPlayer
            v-else
            :video="(photo as SwipeVideo)"
            no-click
            @click="openLightBox(index, true)"
          />
        </div>
      </div>

      <div
        v-else
        :class="['relative', computedAspectRatioClass]"
      >
        <TSwipeImages
          :contents="images"
          @content-viewed="updateIndex"
          @content-clicked="openLightBox"
        />
      </div>

      <div
        v-if="images"
        class="absolute lg:hidden bottom-4 right-4 z-2 text-white text-xs rounded-full py-1 px-2 bg-primary-950/80"
       
      >
        {{ t('countImages', { count: currentCarouselIndex + 1, total: images.length }) }}
      </div>

      <div class="absolute z-2 left-5 bottom-5 flex max-lg:flex-col gap-2">
        <slot name="tour" />

        <!-- view-all -->
        <div class="hidden lg:block">
          <TButton
            outline
            size="sm"
           
            :data-test-count="images.length"
            @click="openLightBox(0)"
          >
            <TIcon
              :icon="['far', 'camera']"
              class="mr-2"
            />{{ t('viewAllPhotos', images.length) }}
          </TButton>
        </div>
      </div>
    </div>

    <AppLightboxImages
      v-if="lightboxOpened"
      :index="imageIndex"
      :images="lightboxImages"
      @mounted="lightboxMounted = true"
      @close="imageIndex = undefined"
    />
  </div>
</template>

<script setup lang="ts">
import type { SwipeContent, SwipeImage, SwipeVideo } from '~/components/_tailwind/swipe/types'

const props = withDefaults(defineProps<{
  images: SwipeContent[]
  aspectRatio?: '16/9' | '21/9' | '3/2' | '4/3'
}>(), {
  aspectRatio: '3/2',
})

const { t } = useI18n()

const computedMosaicGridClasses = computed(() => [
  'overflow-hidden rounded-lg empty:bg-gray-100 empty:aspect-16/9',
  {
    'block': props.images.length < 3,
    'grid gap-2 grid-cols-3 grid-rows-2 [grid-template-areas:"image0_image0_image1"_"image0_image0_image2"]': props.images.length >= 3,
  },
])

const mosaicGridAreaClass = [
  '[grid-area:image0]',
  '[grid-area:image1]',
  '[grid-area:image2]',
]

const computedAspectRatioClass = computed(() => {
  if (mosaicPhotos.value.length > 1) {
    return 'aspect-3/2'
  }

  switch (props.aspectRatio) {
    case '16/9':
      return 'aspect-16/9'
    case '21/9':
      return 'aspect-21/9'
    case '4/3':
      return 'aspect-4/3'
    case '3/2':
    default:
      return 'aspect-3/2'
  }
})

const imageIndex = ref<number | undefined>(undefined)
const currentCarouselIndex = ref(0)

const { isMobile } = useDevice()
const mosaicPhotos = computed(() => {
  if (isMobile) {
    return props.images.slice(0, 1)
  }

  return props.images.length >= 3 ? props.images.slice(0, 3) : props.images.slice(0, 1)
})

const lightboxMounted = ref(false)
const lightboxOpened = ref(false)

const lightboxImages = computed(() => {
  const images = [...props.images]

  // Find the index of the video.
  const videoIndex = images.findIndex((image) => Boolean((image as SwipeVideo).thumbnail))

  // Remove the video from the array.
  const [video] = images.splice(videoIndex, 1)

  // Put it at the end of the array.
  if (video) {
    images.push(video)
  }

  return images
})

function openLightBox(index: number = 0, isVideo = false) {
  // Because the video is moved to the last position in the lightbox, we
  // need to adjust the index.
  const lightboxIndex = isVideo ? props.images.length - 1 : index

  lightboxOpened.value = true

  nextTick(() => {
    imageIndex.value = lightboxIndex
    currentCarouselIndex.value = lightboxIndex
  })
}

function updateIndex(index: number) {
  currentCarouselIndex.value = index
}
</script>

<i18n lang="json">
{
  "en": {
    "countImages": "{count} of {total}",
    "viewAllPhotos": "View 1 photo | View all {n} photos"
  },
  "fr": {
    "countImages": "{count} sur {total}",
    "viewAllPhotos": "Voir 1 photo | Voir toutes les {n} photos"
  }
}
</i18n>
